import React, { useEffect, useState } from "react";
import axios from "axios";
import { useApp } from "../contexts/AppContext";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { 
  Plus, Pencil, Trash2, Building2, MapPin, Hotel, 
  Utensils, Warehouse, Briefcase 
} from "lucide-react";
import { LOCATION_TYPES, API_URL } from "../lib/utils";


const TYPE_ICONS = {
  hotel: Hotel,
  ristorante: Utensils,
  lavanderia: Warehouse,
  appalto: Briefcase
};

export default function CompaniesPage() {
  const { isAdmin } = useAuth();
  const { companies, locations, fetchCompanies, fetchLocations } = useApp();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("companies");
  
  // Company dialog
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({ name: "", description: "", logo_url: "" });
  
  // Location dialog
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [locationForm, setLocationForm] = useState({ name: "", city: "", company_id: "", type: "hotel", logo_url: "" });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    await fetchCompanies();
    await fetchLocations();
    setLoading(false);
  };

  // Company handlers
  const handleOpenCompanyDialog = (company = null) => {
    if (company) {
      setEditingCompany(company);
      setCompanyForm({ name: company.name, description: company.description || "", logo_url: company.logo_url || "" });
    } else {
      setEditingCompany(null);
      setCompanyForm({ name: "", description: "", logo_url: "" });
    }
    setCompanyDialogOpen(true);
  };

  const handleSaveCompany = async () => {
    try {
      if (editingCompany) {
        await axios.put(`${API_URL}/api/companies/${editingCompany.id}`, companyForm, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/companies`, companyForm, { withCredentials: true });
      }
      setCompanyDialogOpen(false);
      fetchCompanies();
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteCompany = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa azienda?")) return;
    try {
      await axios.delete(`${API_URL}/api/companies/${id}`, { withCredentials: true });
      fetchCompanies();
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    }
  };

  // Location handlers
  const handleOpenLocationDialog = (location = null) => {
    if (location) {
      setEditingLocation(location);
      setLocationForm({ 
        name: location.name, 
        city: location.city, 
        company_id: location.company_id, 
        type: location.type,
        logo_url: location.logo_url || ""
      });
    } else {
      setEditingLocation(null);
      setLocationForm({ name: "", city: "", company_id: "", type: "hotel", logo_url: "" });
    }
    setLocationDialogOpen(true);
  };

  const handleSaveLocation = async () => {
    try {
      if (editingLocation) {
        await axios.put(`${API_URL}/api/locations/${editingLocation.id}`, locationForm, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/locations`, locationForm, { withCredentials: true });
      }
      setLocationDialogOpen(false);
      fetchLocations();
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteLocation = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa sede?")) return;
    try {
      await axios.delete(`${API_URL}/api/locations/${id}`, { withCredentials: true });
      fetchLocations();
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    }
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || "-";
  };

  const getLocationsByCompany = (companyId) => {
    return locations.filter(l => l.company_id === companyId);
  };

  return (
    <div className="space-y-6" data-testid="companies-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-outfit tracking-tight text-[#09090B]">
            Aziende e Sedi
          </h1>
          <p className="text-[#52525B] font-ibm-plex mt-1">
            Gestisci la struttura organizzativa
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="companies" className="font-ibm-plex" data-testid="companies-tab">
            <Building2 className="w-4 h-4 mr-2" />
            Aziende
          </TabsTrigger>
          <TabsTrigger value="locations" className="font-ibm-plex" data-testid="locations-tab">
            <MapPin className="w-4 h-4 mr-2" />
            Sedi
          </TabsTrigger>
        </TabsList>

        <TabsContent value="companies" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button
              data-testid="add-company-button"
              onClick={() => handleOpenCompanyDialog()}
              className="bg-black hover:bg-gray-800 text-white font-ibm-plex"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuova Azienda
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {loading ? (
              <p className="col-span-full text-center text-[#52525B] py-8">Caricamento...</p>
            ) : companies.length === 0 ? (
              <p className="col-span-full text-center text-[#52525B] py-8">Nessuna azienda trovata</p>
            ) : (
              companies.map(company => (
                <Card key={company.id} className="border border-[#E4E4E7] hover:shadow-md transition-shadow" data-testid={`company-card-${company.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        {company.logo_url ? (
                          <img src={company.logo_url} alt={company.name} className="w-10 h-10 rounded object-cover" />
                        ) : (
                          <div className="w-10 h-10 bg-black rounded flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-white" />
                          </div>
                        )}
                        <CardTitle className="text-lg font-outfit">{company.name}</CardTitle>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenCompanyDialog(company)} data-testid={`edit-company-${company.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {isAdmin && (
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteCompany(company.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-company-${company.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-[#52525B] font-ibm-plex mb-3">{company.description || "Nessuna descrizione"}</p>
                    <div className="text-xs text-[#52525B] font-ibm-plex">
                      <MapPin className="w-3 h-3 inline mr-1" />
                      {getLocationsByCompany(company.id).length} sedi
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="locations" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button
              data-testid="add-location-button"
              onClick={() => handleOpenLocationDialog()}
              className="bg-black hover:bg-gray-800 text-white font-ibm-plex"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuova Sede
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {loading ? (
              <p className="col-span-full text-center text-[#52525B] py-8">Caricamento...</p>
            ) : locations.length === 0 ? (
              <p className="col-span-full text-center text-[#52525B] py-8">Nessuna sede trovata</p>
            ) : (
              locations.map(location => {
                const TypeIcon = TYPE_ICONS[location.type] || MapPin;
                const typeLabel = LOCATION_TYPES.find(t => t.value === location.type)?.label || location.type;
                return (
                  <Card key={location.id} className="border border-[#E4E4E7] hover:shadow-md transition-shadow" data-testid={`location-card-${location.id}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          {location.logo_url ? (
                            <img src={location.logo_url} alt={location.name} className="w-10 h-10 rounded object-cover" />
                          ) : (
                            <div className="w-10 h-10 bg-[#0EA5E9] rounded flex items-center justify-center">
                              <TypeIcon className="w-5 h-5 text-white" />
                            </div>
                          )}
                          <div>
                            <CardTitle className="text-lg font-outfit">{location.name}</CardTitle>
                            <p className="text-xs text-[#52525B] font-ibm-plex">{typeLabel}</p>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleOpenLocationDialog(location)} data-testid={`edit-location-${location.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {isAdmin && (
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteLocation(location.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-location-${location.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-1 text-sm font-ibm-plex">
                        <p className="text-[#52525B]">
                          <MapPin className="w-3 h-3 inline mr-1" />
                          {location.city}
                        </p>
                        <p className="text-[#52525B]">
                          <Building2 className="w-3 h-3 inline mr-1" />
                          {getCompanyName(location.company_id)}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Company Dialog */}
      <Dialog open={companyDialogOpen} onOpenChange={setCompanyDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit">
              {editingCompany ? "Modifica Azienda" : "Nuova Azienda"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="font-ibm-plex">Nome Azienda *</Label>
              <Input
                data-testid="company-name-input"
                value={companyForm.name}
                onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                className="font-ibm-plex"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Descrizione</Label>
              <Input
                data-testid="company-description-input"
                value={companyForm.description}
                onChange={(e) => setCompanyForm({ ...companyForm, description: e.target.value })}
                className="font-ibm-plex"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">URL Logo</Label>
              <Input
                data-testid="company-logo-input"
                value={companyForm.logo_url}
                onChange={(e) => setCompanyForm({ ...companyForm, logo_url: e.target.value })}
                className="font-ibm-plex"
                placeholder="https://..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompanyDialogOpen(false)} className="font-ibm-plex">
              Annulla
            </Button>
            <Button 
              data-testid="save-company-button"
              onClick={handleSaveCompany} 
              className="bg-black hover:bg-gray-800 text-white font-ibm-plex"
              disabled={!companyForm.name}
            >
              {editingCompany ? "Salva" : "Crea"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Location Dialog */}
      <Dialog open={locationDialogOpen} onOpenChange={setLocationDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit">
              {editingLocation ? "Modifica Sede" : "Nuova Sede"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="font-ibm-plex">Nome Sede *</Label>
              <Input
                data-testid="location-name-input"
                value={locationForm.name}
                onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })}
                className="font-ibm-plex"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Città *</Label>
              <Input
                data-testid="location-city-input"
                value={locationForm.city}
                onChange={(e) => setLocationForm({ ...locationForm, city: e.target.value })}
                className="font-ibm-plex"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Azienda *</Label>
              <Select value={locationForm.company_id} onValueChange={(v) => setLocationForm({ ...locationForm, company_id: v })}>
                <SelectTrigger data-testid="location-company-select" className="font-ibm-plex">
                  <SelectValue placeholder="Seleziona azienda" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Tipo *</Label>
              <Select value={locationForm.type} onValueChange={(v) => setLocationForm({ ...locationForm, type: v })}>
                <SelectTrigger data-testid="location-type-select" className="font-ibm-plex">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LOCATION_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">URL Logo</Label>
              <Input
                data-testid="location-logo-input"
                value={locationForm.logo_url}
                onChange={(e) => setLocationForm({ ...locationForm, logo_url: e.target.value })}
                className="font-ibm-plex"
                placeholder="https://..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLocationDialogOpen(false)} className="font-ibm-plex">
              Annulla
            </Button>
            <Button 
              data-testid="save-location-button"
              onClick={handleSaveLocation} 
              className="bg-black hover:bg-gray-800 text-white font-ibm-plex"
              disabled={!locationForm.name || !locationForm.city || !locationForm.company_id}
            >
              {editingLocation ? "Salva" : "Crea"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
